import type { FaqGroup } from './types';

/**
 * The questions the screens actually provoke — mostly "why can't I see / do this?", which is
 * almost always a precondition rather than a fault. Each answer links into the handbook or
 * glossary rather than repeating them.
 */
export const FAQ: FaqGroup[] = [
  {
    id: 'q-sale',
    title: 'İhale yönetimi',
    items: [
      {
        id: 'f-live-not-bidding',
        q: 'İhale “Devam ediyor” görünüyor ama hiçbir araç teklif almıyor. Neden?',
        a: 'İhale listeleme tarihinde yayına çıkar, araçlar ise ihale başlangıç tarihinde teklife açılır — ikisi aynı an değildir. Aradaki sürede ihale vitrinde durur ama teklif alınmaz; bu pencerede ekranda “Hazırlanıyor” yazar. Başlangıç saatini ihale sayfasından kontrol edin.',
        see: ['g-listing-date', 'g-start-date', 'g-preparing'],
      },
      {
        id: 'f-cannot-edit',
        q: 'Yayınlanmış ihalede “Düzenle” seçeneğini göremiyorum.',
        a: 'Düzenleme yalnızca ihale “Hazırlanıyor” durumundayken ve listeleme zamanı henüz gelmemişken açıktır. İhale vitrine düştükten sonra katılımcılar şartları görmüş olur ve düzenleme kapanır. Zorunlu bir değişiklik varsa ihaleyi iptal edip yenisini kurmanız gerekir.',
        see: ['h-sale-edit-window', 'h-sale-cancel'],
      },
      {
        id: 'f-pause-visible',
        q: 'İhaleyi duraklattım — katılımcılar ne görüyor?',
        a: 'Giriş yapmış katılımcılar “Duraklatıldı” etiketini ve ihale sayfasındaki uyarıyı görür; sayaç donar, teklif alınmaz. Duraklamanın *nedenini* hiçbir ekran yazmaz — gerekiyorsa konferanstan ya da ayrıca duyurun. Giriş yapmamış ziyaretçi ihaleyi zaten hiç görmez (13.08.2026 kararı), dolayısıyla duraklamayı da görmez.',
        see: ['g-paused', 'h-live-pause'],
      },
      {
        id: 'f-resume-schedule',
        q: 'Devam ettirdiğimde saatler kaydı — bu normal mi?',
        a: 'Evet. Duraklatma sayacı dondurur; devam ettirdiğinizde kalan araçların açılış ve kapanış saatleri duraklama süresi kadar ileri alınır. Aksi hâlde duraklatılan süre araçların teklif süresinden yenirdi.',
        see: ['h-live-pause'],
      },
      {
        id: 'f-vehicle-missing-picker',
        q: 'İhale kurarken aradığım aracı listede bulamıyorum.',
        a: 'Araç zaten başka bir ihaleye alınmış olabilir — araç listesinde durumu “İhalede” görünür ve seçilebilir araçlar arasına girmez. Bir araç aynı anda yalnızca bir ihalede yer alabilir.',
        see: ['h-sale-create'],
      },
    ],
  },

  {
    id: 'q-bidding',
    title: 'Teklif ve fiyat',
    items: [
      {
        id: 'f-hammer-missing',
        q: '“Çekiç” düğmesini göremiyorum.',
        a: 'Çekiç üç şart birden sağlandığında görünür: ihale devam ediyor olmalı (duraklatılmış değil), araç açık olmalı ve o aracın kontrolünü almış olmalısınız. En sık atlanan adım sonuncusudur — önce “Kontrolü al”a basın.',
        see: ['h-live-hammer', 'g-take-control', 'g-hammer'],
      },
      {
        id: 'f-reserve-visible',
        q: 'Rezervi katılımcılar görüyor mu?',
        a: 'Hayır. Rezerv hiçbir katılımcı ekranında, listede veya dışa aktarımda görünmez. Katılımcının dolaylı olarak anladığı tek şey, rezerv geçildiğinde beliren “Kesin satış” rozetidir.',
        see: ['g-reserve', 'g-guaranteed'],
      },
      {
        id: 'f-jump-vs-free',
        q: '“Hedefe yükselt” ile “Serbest” arasındaki fark ne?',
        a: '“Hedefe yükselt” bareme sadık kalır: girdiğiniz hedefi aşmayan, adımlara uygun en yüksek tutarı yazar. “Serbest” ise baremi hiç dikkate almaz, ne yazarsanız onu girer — bu yüzden yalnızca Yönetici ve Geliştirici kullanabilir ve teklif geçmişinde ayrı işaretlenir.',
        see: ['g-jump', 'g-free-bid', 'h-live-bidding'],
      },
      {
        id: 'f-increment-changed',
        q: 'Artış miktarı ihale sırasında kendiliğinden değişti.',
        a: 'Beklenen davranıştır. Artış bareme bağlıdır ve fiyat bir üst banda geçtiğinde adım da büyür. Ayrıca rezerv geçildikten sonra farklı bir adım tanımlanmış olabilir. Tek bir araçta sabit adım istiyorsanız araç kartındaki “Artış miktarı” alanını doldurun.',
        see: ['g-barem', 'g-increment', 'h-vehicle-pricing'],
      },
      {
        id: 'f-readd',
        q: 'Satılmayan aracı aynı ihalede tekrar açabilir miyim?',
        a: 'Evet — “Satılmadı” durumundaki aracın kartında “Tekrar ekle” çıkar. Araç ihaleye yeni bir sıra olarak girer; isterseniz kesin satış olarak veya daha düşük bir rezervle ekleyebilirsiniz. Duraklatılmış bir ihalede bu yalnızca sıralı düzenlerde mümkündür; paralel düzende önce “Devam Et” demeniz gerekir.',
        see: ['h-live-readd', 'g-unsold'],
      },
      {
        id: 'f-lot-closed-early',
        q: 'Bir araç süresi dolmadan kapandı.',
        a: 'Ya bir yetkili çekiç kullandı ya da araç ihaleden çıkarıldı. Her iki işlem de denetim kaydına kimin yaptığıyla birlikte yazılır — orada doğrulayabilirsiniz.',
        see: ['h-live-hammer', 'h-live-remove', 'h-audit'],
      },
    ],
  },

  {
    id: 'q-payment',
    title: 'Ödeme ve teminat',
    items: [
      {
        id: 'f-refund-blocked',
        q: 'Teminatı iade edemiyorum, işlem reddediliyor.',
        a: 'En sık sebep, kullanıcının o araçta en yüksek teklifin sahibi olmasıdır — iade lotun kapanmasını bekler. Diğer olasılıklar: teminat zaten iade edilmiş ya da iptal edilmiş olabilir, veya aynı teminat için başka bir iade hâlâ sürüyordur. Ekrandaki mesaj hangisi olduğunu söyler.',
        see: ['h-refund', 'g-deposit-states'],
      },
      {
        id: 'f-reconcile-badge',
        q: 'Teminatta “Mutabakat bekliyor” yazıyor. Ne yapmalıyım?',
        a: 'İade sırasında bankadan yanıt alınamamış demektir; para gitmiş de olabilir, gitmemiş de. Satırdaki “Mutabakat” düğmesiyle bankaya durum sorulur ve kayıt doğru duruma alınır. Banka net yanıt vermezse durum değişmez — banka panelinden kontrol edip 10 dakika sonra tekrar deneyin.',
        see: ['g-reconcile', 'h-refund'],
      },
      {
        id: 'f-stuck-3ds',
        q: 'Katılımcı “ödeme yapamıyorum” diyor ama kartında sorun yok.',
        a: 'Ödemesi büyük ihtimalle “3DS sürüyor” durumunda takılı kalmıştır: banka doğrulaması bize geri dönmemiştir. Bu kayıt çözülene kadar kullanıcı yeni ödeme başlatamaz. Kullanıcı detay sayfasındaki Ödemeler bölümünden “Mutabakat”ı çalıştırın; günlük otomatik tarama da bu kayıtları toparlar.',
        see: ['h-payments', 'g-3ds'],
      },
      {
        id: 'f-mark-failed',
        q: '“Başarısız işaretle”yi ne zaman kullanmalıyım?',
        a: 'Yalnızca bankada hiç tahsilat olmadığını banka panelinden doğruladıysanız. Tahsilat varken kullanmak çifte tahsilata yol açar. Önce her zaman “Mutabakat”ı deneyin; bu düğme son çaredir.',
        see: ['h-payments'],
      },
      {
        id: 'f-membership-deposit',
        q: 'Üyeliği olan katılımcı teminat yatırmadan teklif verebiliyor. Doğru mu?',
        a: 'Evet. Ücretli üyelik teminat şartını kaldırır. Teklif verme kurallarının geri kalanı (ihalenin açık olması, aracın sırasının gelmesi) aynen geçerlidir.',
        see: ['g-membership', 'g-deposit'],
      },
    ],
  },

  {
    id: 'q-vehicle',
    title: 'Araç ve görseller',
    items: [
      {
        id: 'f-price-locked',
        q: 'Araç kartında fiyat alanları kapalı, değiştiremiyorum.',
        a: 'Araç bir ihaleye alınmış demektir. İhaledeki araç, oluşturulduğu andaki fiyatı üzerine yazar; kartı değiştirmek işleyen ihalenin fiyatını değiştirmez, bu yüzden alanlar kilitlenir. Açıklama, teknik bilgiler ve görseller düzenlenmeye açık kalır ve canlı ihaleye yansır. Fiyatı gerçekten değiştirmeniz gerekiyorsa canlı konsoldan rezerv ya da teklif düzeyinde müdahale edin.',
        see: ['h-vehicle-pricing', 'h-live-reserve'],
      },
      {
        id: 'f-original-badge',
        q: '“Orijinal” rozetini nasıl veririm?',
        a: 'Elle verilmez. Rozet, ekspertiz raporundaki bütün parçalar “orijinal” işaretlendiğinde kendiliğinden çıkar. Tek bir parça bile farklı bir durumdaysa ya da boş bırakılmışsa rozet görünmez.',
        see: ['g-expertise', 'h-vehicle-expertise'],
      },
      {
        id: 'f-photo-not-public',
        q: 'Fotoğrafı “herkese açık” yaptım ama giriş yapmamış ziyaretçi göremiyor.',
        a: 'Göremez — beklenen davranış bu. 13.08.2026’dan beri açık artırmaların tamamı üyelere özeldir: giriş yapmamış bir ziyaretçi ihale listesini, araçları, fotoğrafları ve satıldı/satılmadı bilgisini hiç görmez. “Herkese açık” işareti şu anda kimseye erişim **vermez**; yayımlanabilirlik kaydı olarak durur. Giriş yapmış bir üye ise, işaret koyulmuş olsun ya da olmasın, araca eklediğiniz bütün görselleri görür.',
        see: ['g-public-media', 'g-company-gated', 'h-vehicle-media'],
      },
      {
        id: 'f-cannot-delete-vehicle',
        q: 'Aracı silemiyorum.',
        a: 'Araç daha önce bir ihaleye girmişse silinemez — silinmesi geçmiş ihale kayıtlarını bozardı. Bu araçlar sistemde kalır; yeni ihalelere eklememeniz yeterlidir.',
        see: ['h-vehicle-create'],
      },
      {
        id: 'f-submission-photos',
        q: 'Başvuruda “görsel aktarılamadı” uyarısı var. Önemli mi?',
        a: 'Evet. İş ortağının gönderdiği bazı fotoğraflar sisteme alınamamış demektir; elinizdeki görsel sayısı gönderilenden az olabilir. Kararı vermeden önce eksik görselleri iş ortağından isteyin.',
        see: ['h-submissions'],
      },
    ],
  },

  {
    id: 'q-access',
    title: 'Erişim ve yetki',
    items: [
      {
        id: 'f-button-missing',
        q: 'Bir düğmeyi hiç göremiyorum — arıza mı?',
        a: 'Büyük ihtimalle yetki. Her işlem belirli rollere açıktır ve rolünüz uygun değilse düğme ekranda hiç çıkmaz. Kılavuzdaki her başlıkta o işlemi hangi rollerin yapabildiği yazılıdır; ihtiyacınız olan yetki yoksa bir yöneticiden rol talep edin.',
        see: ['h-users'],
      },
      {
        id: 'f-roles-additive',
        q: 'Bir kullanıcıya birden çok rol verebilir miyim?',
        a: 'Evet. Roller birikimlidir ve aralarında hiyerarşi yoktur: kullanıcı, taşıdığı rollerin herhangi birine açık olan her işlemi yapabilir.',
        see: ['h-users'],
      },
      {
        id: 'f-password-sessions',
        q: 'Bir kullanıcının parolasını değiştirdim, ne oluyor?',
        a: 'Kullanıcının açık bütün oturumları sonlandırılır ve yeniden giriş yapması gerekir. Bu, parola değişikliğinin eski oturumları açık bırakmamasını güvence altına alır.',
        see: ['h-users'],
      },
      {
        id: 'f-firm-cannot-submit',
        q: 'İş ortağı “araç gönderemiyorum” diyor, ne kontrol etmeliyim?',
        a: 'Sırayla: firmanın anahtarı iptal edilmiş mi ya da firma arşivlenmiş mi (Firmalar → firma sayfası — ikisi de anahtarı anında geçersiz kılar), hedefledikleri ihale hâlâ gönderime açık mı (listeleme saati geçmiş ihale kapanır), ve ihale o firmaya açık mı. Anahtarı kaybettilerse kurtarılamaz; yeni bir anahtar üretip iletin, sonra eskisini iptal edin.',
        see: ['h-firms', 'g-api-key', 'h-submissions'],
      },
      {
        id: 'f-login-blocked',
        q: 'Kullanıcı “giriş yapamıyorum” diyor, ne kontrol etmeliyim?',
        a: 'Önce hesabın onaylı olup olmadığına bakın: siteden kendi kaydını yapan biri onaylanana kadar giriş yapamaz. Kullanıcılar sayfasında **Bekleyen** ve **Reddedilen** sekmelerinde arayın — oradaysa ekranda gördüğü uyarı parola hatası değil, onay uyarısıdır. Hesap “Onaylı” görünüyorsa sorun parolada olabilir; parolayı yenilediğinizde kullanıcının açık bütün oturumları kapanır.',
        see: ['h-users-approval', 'g-approval-pending', 'f-password-sessions'],
      },
      {
        id: 'f-who-did-this',
        q: 'Bir değişikliği kimin yaptığını nasıl bulurum?',
        a: 'Denetim kaydından. İhale ve kullanıcı sayfalarındaki “Denetim kaydı” bağlantısı sizi doğrudan o kayda filtrelenmiş görünüme götürür; oradan işlemin öncesi ve sonrası değerlerini de görebilirsiniz.',
        see: ['h-audit', 'g-audit'],
      },
    ],
  },
];
